import type { ReactElement } from "react"
import { useContext, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import CheckIcon from "@mui/icons-material/Check"
import RemoveIcon from "@mui/icons-material/Remove"
import { Box, Skeleton } from "@mui/material"
import Grid from "@mui/material/Grid"
import { Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosResponse } from "axios"
import type { MRT_ColumnDef, MRT_PaginationState } from "material-react-table"
import moment from "moment"

import { KonanJsonView } from "../../../components/data-display/KonanJsonView"
import { ModelType } from "../../../components/status/ModelType"
import { BaseTableContainer } from "../../../components/tables/BaseTableContainer"
import { getTheme } from "../../../hooks/UseTheme"
import { useDateQuery } from "../../../hooks/useDateQuery"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { ModelComparison } from "../../../types/custom/projects"
import type { ListModelOutputs } from "../../../types/generated/api/ListModelOutputs"
import type { PaginatedListModelOutputsList } from "../../../types/generated/api/PaginatedListModelOutputsList"
import { TableConfigurationRequest } from "../../../types/generated/api/TableConfigurationRequest"
import { compareModels } from "../../../utils/modelDetailsHelpers"
import { ModelComparisonTableContext } from "../Context"
import type { ComparisonTableProps } from "../interfaces"

/**
 * Table component for comparing  differences between model properties and metrics in a tabular format
 * @returns {ReactElement} Table showing model comparison data
 */
function ComparisonTable(): ReactElement {
  const { pagination, setPagination, modelA, modelB } = useContext(ModelComparisonTableContext)

  const theme = getTheme()
  const { t } = useTranslation("models")

  // State for storing processed comparison data
  const [comparedData, setComparedData] = useState<ModelComparison[]>([])

  /**
   * Column definitions for the comparison table
   * Includes property names, metrics, and visual indicators
   */
  const columns: MRT_ColumnDef<ModelComparison>[] = [
    {
      header: t("comparison.table.headers.request_date"),
      size: 20,
      accessorKey: "created_at",
      accessorFn: (row) => moment(row.created_at).utc().format("DD/MM/YYYY"),
    },
    {
      header: t("comparison.table.headers.request_time_utc"),
      accessorKey: "created_at_utc",
      maxSize: 20,
      size: 20,
      enableResizing: true,
      accessorFn: (row) => moment(row.created_at).utc().format("hh:mm:ss A"),
    },
    {
      header: t("comparison.table.headers.feedback"),
      accessorKey: "feedback",
      size: 20,
      enableColumnFilter: false,
      // Display a check icon if feedback exists, otherwise a remove icon
      Cell: ({ row }) => (row.original.feedback ? <CheckIcon /> : <RemoveIcon />),
    },
    {
      header: t("comparison.table.headers.model_a_status_code"),
      accessorKey: "model_a_status_code",
      accessorFn: (row) => row.model_a_status_code?.toString(),
      size: 150,
      // Make error's color darker than success to emphasize the success rows
      Cell: ({ row }) => {
        if (row.original.model_a_status_code !== undefined && row.original.model_a_status_code !== null) {
          return (
            <span
              style={{
                color:
                  row.original.model_a_status_code >= 400 ? theme.palette.red.text[2] : theme.palette.green.text[2],
              }}
            >
              {row.original.model_a_status_code}
            </span>
          )
        } else {
          return <span style={{ color: theme.palette.yellow.text[2] }}>{t("comparison.table.na")}</span>
        }
      },
    },
    {
      header: t("comparison.table.headers.model_b_status_code"),
      accessorKey: "model_b_status_code",
      accessorFn: (row) => row.model_b_status_code?.toString(),
      size: 150,
      // Make error's color darker than success to emphasize the success rows
      Cell: ({ row }) => {
        if (row.original.model_b_status_code !== undefined && row.original.model_b_status_code !== null) {
          return (
            <span
              style={{
                color:
                  row.original.model_b_status_code >= 400 ? theme.palette.red.text[2] : theme.palette.green.text[2],
              }}
            >
              {row.original.model_b_status_code}
            </span>
          )
        } else {
          return <span style={{ color: theme.palette.yellow.text[2] }}>{t("comparison.table.na")}</span>
        }
      },
    },
    {
      header: t("comparison.table.headers.model_a_response_time"),
      accessorKey: "model_a_response_time",
      // Display model A's response time in milliseconds or "NA" if not available
      accessorFn: (row) => {
        if (row.model_a_response_time) return row.model_a_response_time + " ms"
        else {
          return <span style={{ color: theme.palette.yellow.text[2] }}>{t("comparison.table.na")}</span>
        }
      },
      size: 150,
    },
    {
      header: t("comparison.table.headers.model_b_response_time"),
      accessorKey: "model_b_response_time",
      // Display model B's response time in milliseconds or "NA" if not available
      accessorFn: (row) => {
        if (row.model_b_response_time) return row.model_b_response_time + " ms"
        else {
          return <span style={{ color: theme.palette.yellow.text[2] }}>{t("comparison.table.na")}</span>
        }
      },
      size: 150,
    },
  ]

  //reset model A and B pages whenever the user changes the models
  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelA?.model?.uuid, modelB?.model?.uuid])

  // Use useEffect instead of useMemo to prevent scrolling to the top when returning []
  useEffect(() => {
    if ((modelA.response || modelB.response) && !modelA.isFetching && !modelB.isFetching) {
      const modelAData = Array.from(modelA.response ?? [])
      const modelBData = Array.from(modelB.response ?? [])

      // Compare the models and set the compared data
      setComparedData(compareModels(modelAData, modelBData))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelA.response, modelB.response])

  return (
    <BaseTableContainer
      title={
        modelA.isFetching || modelB.isFetching ? (
          <Skeleton animation="wave" height={30} width={300} />
        ) : (
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid item>
              <Typography variant="h3-bold">{modelA?.model?.name + " (A)"}</Typography>
            </Grid>
            <Grid item>
              <ModelType modelState={modelA?.model?.state} />
            </Grid>
            <Grid item>
              <Typography variant="h3-bold">{t("comparison.table.vs_label", { name: modelB?.model?.name })}</Typography>
            </Grid>
            <Grid item>
              <ModelType modelState={modelB?.model?.state} />
            </Grid>
          </Grid>
        )
      }
      columns={columns as MRT_ColumnDef[]}
      source={TableConfigurationRequest.table.MODEL_COMPARISON}
      isLoading={modelA.isFetching || modelB.isFetching}
      data={comparedData}
      pagination={pagination}
      setPagination={setPagination}
      rowCount={Math.max(modelA.totalCount, modelB.totalCount)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      detailPanel={(row: any) => (
        <Box p={2}>
          <Grid container spacing={2}>
            <Grid item xs={row.original.feedback ? 3 : 4}>
              <Typography variant="p" gutterBottom>
                {t("comparison.table.detail.request_body")}
              </Typography>
              <KonanJsonView src={row.original.features} />
            </Grid>
            {row.original.feedback && (
              <Grid item xs={3}>
                <Typography variant="p" gutterBottom>
                  {t("comparison.table.headers.feedback")}
                </Typography>
                <KonanJsonView src={row.original.feedback} />
              </Grid>
            )}
            <Grid item xs={row.original.feedback ? 3 : 4}>
              <Typography variant="p" gutterBottom>
                {t("comparison.table.detail.model_response", { name: modelA?.model?.name })}
              </Typography>
              <KonanJsonView src={row.original.model_a_mls_output} />
            </Grid>
            <Grid item xs={row.original.feedback ? 3 : 4}>
              <Typography variant="p" gutterBottom>
                {t("comparison.table.detail.model_response", { name: modelB?.model?.name })}
              </Typography>
              <KonanJsonView src={row.original.model_b_mls_output} />
            </Grid>
          </Grid>
        </Box>
      )}
    />
  )
}

/**
 * Container component for managing and displaying a model comparison table.
 * Handles data fetching, state management, and context provision for comparing two models.
 *
 * @param {ComparisonTableProps} props - The properties passed to the component.
 * @param {model} modelA - The primary model used for comparison.
 * @param {string} compareModel - The model to compare against the primary model.
 * @returns {React.ReactElement} A React element that renders the model comparison table.
 */
export function ComparisonTableContainer(props: Readonly<ComparisonTableProps>): ReactElement {
  const { modelA, compareModel } = props
  const { id: projectId } = useParams<{ id: string }>()

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  // start and end date state for fetching api request data
  const [startDate, ___, endDate] = useDateQuery()

  // Get current model and project context

  // Pagination state for model comparison table
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  // Query for fetching comparison model's request logs
  // Includes pagination and date range filtering
  const { isLoading: isLoadingModelToCompareWith, data: modelToCompareWithResponse } = useQuery<
    AxiosResponse<PaginatedListModelOutputsList>
  >(
    ["request-log", startDate, endDate, projectId, compareModel, pagination.pageSize, pagination.pageIndex],
    () =>
      KonanAPI.fetchModelRequestLogData(
        startDate?.toISOString() as string,
        endDate?.toISOString() as string,
        compareModel?.uuid as string,
        pagination.pageIndex + 1,
        pagination.pageSize,
      ),
    {
      keepPreviousData: true,
    },
  )

  // reset pagination when date range changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [startDate, endDate])

  return (
    /* Model comparison table context provider */
    <ModelComparisonTableContext.Provider
      value={{
        pagination: pagination,
        setPagination: setPagination,
        modelA: {
          model: currentModel,
          response: modelA.response,
          isFetching: modelA.isFetching,
          totalCount: modelA.totalCount,
        },
        modelB: {
          model: compareModel,
          response: modelToCompareWithResponse?.data?.results as Array<ListModelOutputs>,
          isFetching: isLoadingModelToCompareWith,
          totalCount: modelToCompareWithResponse?.data?.count ?? 0,
        },
      }}
    >
      <ComparisonTable />
    </ModelComparisonTableContext.Provider>
  )
}
